import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { LanguageModule } from '@language';
import {
    BaseContainer,
    ExtendedProfile,
    ExtendedProfileService,
    PartialRecord,
    Profile,
    ProfileHelper,
    RecordInheritance,
    ShNotificationComponent,
    ShProfileFormModule
} from '@stylehaven/components';
import intersection from 'lodash-es/intersection';

export type ProfileExtras = Record<string, Partial<ExtendedProfile>>;

@Component({
    selector: 'med-generic-edit-form',
    templateUrl: './generic-edit-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShNotificationComponent, LanguageModule, ReactiveFormsModule, ShProfileFormModule]
})
export class GenericEditFormContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private visibleProfileIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    public readonly recordsList$: ReplaySubject<PartialRecord[]> = new ReplaySubject<PartialRecord[]>(1);
    public profiles$: Observable<ExtendedProfile[]>;

    // Injectors
    private readonly profileHelper: ProfileHelper = inject(ProfileHelper);

    @Input() set isLoading(isLoading: boolean) {
        this.loading$.next(isLoading);
    }

    @Input() set records(records: PartialRecord | PartialRecord[]) {
        if (!records) {
            return;
        }

        if (Array.isArray(records)) {
            this.recordsList$.next(records);
        } else {
            this.recordsList$.next([records]);
        }
    }

    @Input() recordInheritance?: RecordInheritance;
    @Input() aiProfile?: Profile;

    @Input() form!: UntypedFormGroup;

    @Input() isBatchEdit?: boolean = false;

    @Output() visibleProfiles: EventEmitter<Profile[]> = new EventEmitter<Profile[]>();

    constructor(private profileService: ExtendedProfileService) {
        super();
        this.initProfiles();
    }

    ngOnInit(): void {
        this.setupProfilesEmitter();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.recordsList$.complete();
    }

    private setupProfilesEmitter(): void {
        combineLatest([this.profiles$, this.visibleProfileIds$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([profiles, visibleProfileIds]) => {
                const visibleProfiles = profiles.filter(profile => visibleProfileIds.includes(profile.Id));
                this.visibleProfiles.emit(visibleProfiles);
            });
    }

    private initProfiles(): void {
        this.profiles$ = this.recordsList$.pipe(
            map(records => records.map(record => record.getProfileIds())),
            map(allrecordProfileIds => intersection(...allrecordProfileIds)),
            switchMap(profileIds => this.profileService.getExtended(profileIds)),
            switchMap(profiles => this.profileHelper.filterViewableProfiles(profiles))
        );

        this.profiles$.pipe(takeUntil(this.destroy$)).subscribe(profiles => {
            if (profiles.length === 0) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        });
    }

    public isVisibleProfile(profileId: string, visible: boolean): void {
        const currentVisibleProfileIds = this.visibleProfileIds$.value;

        if (visible) {
            this.visibleProfileIds$.next([...currentVisibleProfileIds, profileId]);
        } else {
            this.visibleProfileIds$.next(currentVisibleProfileIds.filter(id => id !== profileId));
        }
    }
}
